/**
 * Geometry for snake and ladder artwork drawn from the board's own measurements.
 *
 * Laying a fixed drawing over a link means rotating, scaling and cropping art
 * that was never made for that slot, which is what left feet, tails and mouths
 * sitting off-centre. Building each shape from the link instead guarantees a
 * ladder's feet and a snake's tail land dead centre in their squares.
 *
 * Every coordinate is in board cells and matches the SVG viewBox, so one unit
 * is one square no matter how big the board is drawn.
 */

/** Outer rail-to-rail width of a ladder. */
const LADDER_WIDTH = 0.34;
/** Target gap between rungs; the real gap is evened out over the length. */
const LADDER_RUNG_GAP = 0.28;
/** Width of a snake at its thickest. */
const SNAKE_WIDTH = 0.15;
/** How far the body swings off the straight line between the two squares. */
const SNAKE_SWAY = 0.2;
const BODY_STEPS = 64;
/** Spacing (in sample steps) between diamond scale marks along the spine. */
const SCALE_STRIDE = 3;

function direction(from, to) {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = Math.hypot(dx, dy) || 1;
  return { x: dx / length, y: dy / length, length };
}

const round = (n) => Math.round(n * 1000) / 1000;

function toPath(points, close = false) {
  const d = points
    .map((pt, i) => `${i ? "L" : "M"}${round(pt.x)} ${round(pt.y)}`)
    .join(" ");
  return close ? `${d} Z` : d;
}

/**
 * A straight ladder standing on the two cell centres. Rungs are spaced evenly
 * between the feet and the top, and the rails run the full length.
 */
export function ladderShape(foot, top) {
  const along = direction(foot, top);
  const across = { x: -along.y, y: along.x };
  const half = LADDER_WIDTH / 2;

  const rail = (side) => ({
    x1: round(foot.x + across.x * half * side),
    y1: round(foot.y + across.y * half * side),
    x2: round(top.x + across.x * half * side),
    y2: round(top.y + across.y * half * side),
  });

  const gaps = Math.max(3, Math.round(along.length / LADDER_RUNG_GAP));
  const rungs = [];
  for (let i = 1; i < gaps; i += 1) {
    const t = i / gaps;
    const x = foot.x + (top.x - foot.x) * t;
    const y = foot.y + (top.y - foot.y) * t;
    rungs.push({
      x1: round(x - across.x * half),
      y1: round(y - across.y * half),
      x2: round(x + across.x * half),
      y2: round(y + across.y * half),
    });
  }

  return { rails: [rail(-1), rail(1)], rungs };
}

/** Whip-thin at the tail, full through the body, with a defined neck before the head. */
function bodyHalfWidth(t) {
  const grow = Math.pow(t, 0.4);
  const neck = 1 - 0.42 * Math.max(0, (t - 0.82) / 0.18);
  return (SNAKE_WIDTH / 2) * (0.1 + 0.9 * grow) * neck;
}

/** Small four-point scute so the diamond scale pattern reads at a snake's scale. */
function scutePath(rx, ry) {
  return `M0 ${round(-ry)} Q${round(rx)} ${round(-ry * 0.32)} ${round(rx)} 0 Q${round(
    rx
  )} ${round(ry * 0.32)} 0 ${round(ry)} Q${round(-rx)} ${round(ry * 0.32)} ${round(
    -rx
  )} 0 Q${round(-rx)} ${round(-ry * 0.32)} 0 ${round(-ry)} Z`;
}

/**
 * A snake winding from its tail on the lower square to its head on the higher
 * one. Longer snakes get more curves so the body reads as a snake rather than
 * a bent tube.
 */
export function snakeShape(tail, head) {
  const along = direction(tail, head);
  const across = { x: -along.y, y: along.x };
  const turns = Math.max(1, Math.min(1.6, along.length / 1.9));
  const sway = Math.min(SNAKE_SWAY, along.length * 0.12);

  const spine = [];
  for (let i = 0; i <= BODY_STEPS; i += 1) {
    const t = i / BODY_STEPS;
    // The curve fades to nothing at both ends, so the tail and head sit exactly
    // on their cell centres however much the middle wanders.
    const offset =
      Math.sin(2 * Math.PI * turns * t) * sway * Math.sin(Math.PI * t);
    spine.push({
      x: tail.x + along.x * along.length * t + across.x * offset,
      y: tail.y + along.y * along.length * t + across.y * offset,
    });
  }

  const left = [];
  const right = [];
  const highlightLeft = [];
  const highlightRight = [];
  const tangents = [];
  spine.forEach((point, i) => {
    const tangent = direction(
      spine[Math.max(0, i - 1)],
      spine[Math.min(spine.length - 1, i + 1)]
    );
    tangents.push(tangent);
    const normal = { x: -tangent.y, y: tangent.x };
    const half = bodyHalfWidth(i / BODY_STEPS);
    left.push({ x: point.x + normal.x * half, y: point.y + normal.y * half });
    right.push({ x: point.x - normal.x * half, y: point.y - normal.y * half });

    // A slim highlight ribbon riding one flank gives the body a rounded,
    // cylindrical sheen instead of a flat-filled tube.
    const shift = half * 0.32;
    const sheen = half * 0.34;
    const cx = point.x + normal.x * shift;
    const cy = point.y + normal.y * shift;
    highlightLeft.push({ x: cx + normal.x * sheen, y: cy + normal.y * sheen });
    highlightRight.push({ x: cx - normal.x * sheen, y: cy - normal.y * sheen });
  });

  const scales = [];
  for (let i = 4; i <= BODY_STEPS - 3; i += SCALE_STRIDE) {
    const t = i / BODY_STEPS;
    const half = bodyHalfWidth(t);
    if (half < SNAKE_WIDTH * 0.14) continue;
    const point = spine[i];
    const tangent = tangents[i];
    scales.push({
      x: round(point.x),
      y: round(point.y),
      angle: round((Math.atan2(tangent.y, tangent.x) * 180) / Math.PI),
      rx: round(half * 0.6),
      ry: round(half * 1.05),
      alt: Math.round(i / SCALE_STRIDE) % 2 === 0,
    });
  }

  const facing = direction(spine[spine.length - 2], spine[spine.length - 1]);
  const rx = SNAKE_WIDTH * 0.62;
  const ry = SNAKE_WIDTH * 0.44;
  const x = round(head.x);
  const y = round(head.y);

  // Drawn as if the head faces right; the caller rotates the whole group.
  const tipX = round(x + rx * 1.18);
  const backX = round(x - rx * 1.05);
  const headOutline = `M${tipX} ${y} Q${round(x + rx * 0.3)} ${round(
    y - ry * 1.3
  )} ${backX} ${round(y - ry * 0.88)} Q${round(x - rx * 1.28)} ${y} ${backX} ${round(
    y + ry * 0.88
  )} Q${round(x + rx * 0.3)} ${round(y + ry * 1.3)} ${tipX} ${y} Z`;

  const lip = round(x + rx * 0.95);
  const tip = round(x + rx * 1.85);
  const fork = round(x + rx * 2.3);
  const spread = round(ry * 0.55);

  const eyeX = round(x + rx * 0.28);
  const eyeY = round(ry * 0.5);
  const eyeR = round(ry * 0.32);
  const nostrilX = round(x + rx * 0.85);
  const nostrilY = round(ry * 0.28);

  return {
    outline: toPath([...left, ...right.reverse()], true),
    highlight: toPath([...highlightLeft, ...highlightRight.reverse()], true),
    spine: toPath(spine),
    width: SNAKE_WIDTH,
    scales,
    scutePath,
    head: {
      x,
      y,
      rx: round(rx),
      ry: round(ry),
      angle: round((Math.atan2(facing.y, facing.x) * 180) / Math.PI),
      outline: headOutline,
      eye: { x: eyeX, y: eyeY, r: eyeR, pupil: round(eyeR * 0.5) },
      nostril: { x: nostrilX, y: nostrilY, r: round(ry * 0.07) },
      tongue: `M${lip} ${y} L${tip} ${y} M${tip} ${y} L${fork} ${y - spread} M${tip} ${y} L${fork} ${y + spread}`,
    },
  };
}
